import { UserRole } from "../../../constants/userRole";
import { BaseApi } from "../BaseApi";

let API: BaseApi | null = null;

function getApiInstance(): BaseApi {
  if (!API) {
    API = new BaseApi();
  }
  return API;
}

function buildUrl(
  baseUrl: string,
  params?: { [key: string]: string | number }
) {
  if (!params) return baseUrl;
  const queryParams = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    const val = params[key];
    if (val !== undefined && val !== null && val !== "") {
      queryParams.append(key, String(val));
    }
  });
  const qs = queryParams.toString();
  return qs ? `${baseUrl}?${qs}` : baseUrl;
}

/** List users - same pattern as getOrders.
 *
 * Uses getCached (30s TTL) because multiple pages independently call this
 * with `role: 'seller'` / `role: 'delivery'` in their own useEffects on
 * mount — without cache we saw ~14 duplicate requests per navigation for
 * identical params. Mutations below invalidate the cache. */
function getUsers(params: {
  limit: number;
  skip: number;
  keyword?: string;
  role?: string;
  country?: string;
  /** "false" → pending approval only, "true" → approved only. */
  activated?: string;
}) {
  const url = buildUrl("users", params);
  return getApiInstance().getCached(url);
}

const withUserInvalidate = <T>(p: Promise<T>): Promise<T> =>
  p.then((res) => {
    BaseApi.invalidateCache("users");
    return res;
  });

function createUserApi(params: {
  name: string;
  role: UserRole;
  email: string;
  password: string;
  phone: string;
  country?: string;
  accountManager?: string | null;
  commissionRate?: number;
}) {
  const baseUrl = "users";
  const url = buildUrl(baseUrl);
  return withUserInvalidate(getApiInstance().post(url, params));
}

function updateUserPasswordApi(params: { id: string; password: string }) {
  const baseUrl = "users";
  const url = `${baseUrl}/password/${params.id}`;
  return withUserInvalidate(getApiInstance().put(url, params));
}

function updateUserActivationApi(params: { id: string }) {
  const baseUrl = "users";
  const url = `${baseUrl}/activate/${params.id}`;
  return withUserInvalidate(getApiInstance().put(url, params));
}

/** Admin: unlock a locked account (clears lockUntil + failed attempts). */
function unlockUserApi(params: { id: string }) {
  const url = `users/unlock/${params.id}`;
  return withUserInvalidate(getApiInstance().put(url, params));
}

/** Admin: activate several accounts at once. */
function activateUsersApi(params: { ids: string[] }) {
  return withUserInvalidate(getApiInstance().put("users/activate-many", params));
}

function deleteUserApi(params: { ids: string[] }) {
  const baseUrl = "users";
  const url = `${baseUrl}/delete`;
  return withUserInvalidate(getApiInstance().put(url, params));
}

/** Resend the email-verification link to a pending/unverified user. */
function resendVerificationApi(params: { email: string }) {
  return getApiInstance().post("users/resend-email", params);
}

function updateUserApi(params: {
  id: string;
  name: string;
  role: UserRole;
  email: string;
  phone: string;
  package: string;
  isEmailVerified: boolean;
  isActivated: boolean;
  accountManager?: string | null;
  commissionRate?: number;
}) {
  const baseUrl = "users";
  const url = `${baseUrl}/${params.id}`;
  return withUserInvalidate(getApiInstance().put(url, params));
}

/** Seller self-service: request an email change (sends a confirmation link to the new address). */
function requestEmailChangeApi(params: { newEmail: string; password: string }) {
  const url = "users/me/email";
  return getApiInstance().put(url, params);
}

/** Self-service: change own password (returns fresh tokens to keep the session alive). */
function changeOwnPasswordApi(params: {
  currentPassword: string;
  newPassword: string;
}) {
  return getApiInstance().put("users/me/password", params);
}

/** Self-service: account security info for the profile page (last login, last password change...). */
function getMySecurityApi() {
  return getApiInstance().get("users/me/security");
}

function getUser(params: { id: string }) {
  const baseUrl = "users";
  const url = `${baseUrl}/${params.id}`;
  try {
    return getApiInstance().get(url);
  } catch (error) {
    console.error("Error fetching user:", error);
  }
}

function downloadUserApi(params: {
  limit: number;
  skip: number;
  keyword?: string;
  role?: string;
  country?: string;
}) {
  const baseUrl = "users";
  const url = `${baseUrl}/download`;
  try {
    return getApiInstance().downloadFileForm(url, params, "users.xlsx");
  } catch (error) {
    console.error("Error fetching user:", error);
  }
}

function updateUserProfileApi(params: {
  name: string;
  phone: string;
  rib?: string;
  bankName?: string;
  accountHolderName?: string;
}) {
  const baseUrl = "users/profile";
  const url = baseUrl;
  try {
    return getApiInstance().put(url, params);
  } catch (error) {
    console.error("Error updating user profile:", error);
  }
}

/** Compteurs sidebar admin : nouvelles commandes / expéditions depuis dernière visite. */
function getMenuBadges() {
  try {
    return getApiInstance().get("users/me/menu-badges");
  } catch (error) {
    console.error("Error fetching menu badges:", error);
  }
}

export type MenuVisitSection =
  | "orders"
  | "expeditions"
  | "requests"
  | "shippings"
  | "invoices"
  | "users";

/** Marquer la visite admin sur une section (remet le compteur à zéro). */
function postVisitSection(section: MenuVisitSection) {
  try {
    return getApiInstance().put("users/me/visit", { section });
  } catch (error) {
    console.error("Error posting visit section:", error);
  }
}

export {
  getUsers,
  createUserApi,
  getUser,
  updateUserApi,
  updateUserPasswordApi,
  deleteUserApi,
  downloadUserApi,
  updateUserActivationApi,
  unlockUserApi,
  activateUsersApi,
  updateUserProfileApi,
  requestEmailChangeApi,
  changeOwnPasswordApi,
  getMySecurityApi,
  resendVerificationApi,
  getMenuBadges,
  postVisitSection,
};
